import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Search, FileUp, KeyRound } from "lucide-react";
import StateDistrictSelect from "@/components/StateDistrictSelect";

const empty = {
  distributor_name: "", gst_number: "", contact_person: "", mobile: "", email: "",
  address: "", city: "", state: "", district: "", active_status: "active",
  distributor_margin: 0, additional_margin: 0, credit_days: 0, security_deposit: 0,
  payment_terms: "", exclusive: false,
  // Login credentials — creates a distributor user tied to this record
  login_email: "", login_password: "",
};

const REG_COLOR = {
  Registered: "bg-green-100 text-green-800",
  Pending: "bg-amber-100 text-amber-800",
};

const APPROVAL_COLOR = {
  Approved: "bg-green-100 text-green-800",
  Pending: "bg-amber-100 text-amber-800",
  Rejected: "bg-red-100 text-red-700",
};

export default function Distributors() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const isIBO = hasRole(user, "ibo") && !isAdmin;
  const canCreate = hasRole(user, "admin", "backend_executive", "ibo");
  const canEdit = hasRole(user, "admin", "backend_executive");
  const canRegister = hasRole(user, "accountant", "admin");
  const readOnly = hasRole(user, "distributor");
  const [items, setItems] = useState([]);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");
  useEffect(() => {
    const urlQ = searchParams.get("q");
    if (urlQ) setSearch(urlQ);
  }, [searchParams]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  // Accountant "Register Distributor" invoice flow
  const [regOpen, setRegOpen] = useState(false);
  const [regTarget, setRegTarget] = useState(null);
  const [regForm, setRegForm] = useState({ invoice_type: "Proforma", invoice_number: "", invoice_amount: 0, file: null });

  // Admin approval filter (for admin only — filters the list by admin_approval_status)
  const [approvalFilter, setApprovalFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const q = isAdmin && approvalFilter !== "all"
        ? `?admin_approval_status=${encodeURIComponent(approvalFilter)}`
        : "";
      const { data } = await api.get(`/distribution/distributors${q}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [isAdmin, approvalFilter]);
  useEffect(() => { load(); }, [load]);

  const adminApprove = async (d) => {
    try {
      const fd = new FormData();
      fd.append("note", "");
      await api.post(`/distribution/distributors/${d.id}/admin-approve`, fd);
      toast.success("Distributor approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const adminReject = async (d) => {
    const reason = window.prompt("Reason for rejection (optional):", "");
    if (reason === null) return; // cancelled
    try {
      const fd = new FormData();
      fd.append("note", reason);
      await api.post(`/distribution/distributors/${d.id}/admin-reject`, fd);
      toast.success("Distributor rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...empty, ...d }); setEditing(d.id); setOpen(true); };

  const save = async () => {
    if (!form.distributor_name.trim()) return toast.error("Distributor name required");
    if (!form.state) return toast.error("State is required");
    if (!form.district) return toast.error("District is required");
    // For NEW distributors: if login_email provided, require login_password (and vice versa)
    if (!editing) {
      const le = (form.login_email || "").trim();
      const lp = (form.login_password || "").trim();
      if ((le && !lp) || (!le && lp)) {
        return toast.error("Provide both login email and password, or leave both blank.");
      }
    }
    try {
      const payload = { ...form };
      // strip empty email so pydantic doesn't reject it
      if (!payload.email) delete payload.email;
      if (!payload.login_email) delete payload.login_email;
      if (!payload.login_password) delete payload.login_password;
      if (editing) {
        // login fields never edit an existing user via this endpoint
        delete payload.login_email;
        delete payload.login_password;
        await api.put(`/distribution/distributors/${editing}`, payload);
      } else {
        await api.post("/distribution/distributors", payload);
      }
      toast.success(editing ? "Distributor updated" : "Distributor created (Pending registration)");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openRegister = (d) => {
    setRegTarget(d);
    setRegForm({ invoice_type: "Proforma", invoice_number: "", invoice_amount: 0, file: null });
    setRegOpen(true);
  };

  const submitRegister = async () => {
    if (!regTarget) return;
    if (!regForm.invoice_number.trim()) return toast.error("Invoice number is required");
    try {
      const fd = new FormData();
      fd.append("invoice_type", regForm.invoice_type);
      fd.append("invoice_number", regForm.invoice_number);
      fd.append("invoice_amount", String(regForm.invoice_amount || 0));
      if (regForm.file) fd.append("file", regForm.file);
      await api.post(`/distribution/distributors/${regTarget.id}/register`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Distributor registered");
      setRegOpen(false);
      setRegTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) =>
      (d.distributor_name || "").toLowerCase().includes(q) ||
      (d.distributor_code || "").toLowerCase().includes(q) ||
      (d.gst_number || "").toLowerCase().includes(q) ||
      (d.city || "").toLowerCase().includes(q) ||
      (d.mobile || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">Distributors</h1>
          <p className="text-sm text-muted-foreground">Master list of distributors with commercial terms.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-3 text-muted-foreground" />
            <Input
              placeholder="Search name, code, GST, city, mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-64"
              data-testid="dm-distributor-search"
            />
          </div>
          {canCreate && !readOnly && (
            <Button onClick={openNew} data-testid="dm-add-distributor-btn"><Plus className="w-4 h-4 mr-1" /> {isIBO ? "Register Distributor" : "Add Distributor"}</Button>
          )}
        </div>
      </div>

      {isIBO && (
        <Card className="p-3 bg-amber-50 border-amber-200 text-xs text-amber-900" data-testid="ibo-approval-notice">
          Distributors you register are queued for admin approval before the accountant can invoice them.
        </Card>
      )}

      {isAdmin && (
        <div className="flex flex-wrap gap-2" data-testid="dm-approval-filter">
          {[
            { key: "all", label: "All" },
            { key: "Pending", label: "Pending admin approval" },
            { key: "Approved", label: "Approved" },
            { key: "Rejected", label: "Rejected" },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setApprovalFilter(f.key)}
              data-testid={`dm-filter-${f.key.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                approvalFilter === f.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white text-foreground border-border hover:bg-muted"
              }`}
            >{f.label}</button>
          ))}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>City</TableHead>
                <TableHead>State</TableHead>
                <TableHead>GST</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Margin %</TableHead>
                <TableHead>Reg</TableHead>
                <TableHead>Approval</TableHead>
                <TableHead>Status</TableHead>
                {!readOnly && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8" data-testid="dm-distributors-empty">No distributors match.</TableCell></TableRow>
              )}
              {filtered.map((d) => {
                const approval = d.admin_approval_status || "Approved";
                const isPending = approval === "Pending";
                return (
                <TableRow key={d.id} data-testid={`dm-distributor-row-${d.id}`}>
                  <TableCell className="font-mono text-xs">{d.distributor_code || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="font-medium">
                    {d.distributor_name}
                    {d.created_by_role === "ibo" && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">by IBO</span>
                    )}
                  </TableCell>
                  <TableCell>{d.city || "—"}</TableCell>
                  <TableCell>{d.state || "—"}</TableCell>
                  <TableCell className="text-xs">{d.gst_number || "—"}</TableCell>
                  <TableCell className="text-xs">{d.mobile || "—"}</TableCell>
                  <TableCell className="text-xs">{d.distributor_margin}%</TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 text-[10px] rounded ${REG_COLOR[d.registration_status] || "bg-gray-100 text-gray-700"}`}>
                      {d.registration_status || "Pending"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      data-testid={`dm-approval-${d.id}`}
                      className={`inline-block px-2 py-0.5 text-[10px] rounded ${APPROVAL_COLOR[approval] || "bg-gray-100 text-gray-700"}`}
                    >
                      {approval}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{d.active_status}</TableCell>
                  {!readOnly && (
                    <TableCell className="whitespace-nowrap">
                      {isAdmin && isPending && (
                        <>
                          <Button
                            size="sm"
                            className="mr-1"
                            onClick={() => adminApprove(d)}
                            data-testid={`dm-admin-approve-${d.id}`}
                          >Approve</Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mr-1 border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => adminReject(d)}
                            data-testid={`dm-admin-reject-${d.id}`}
                          >Reject</Button>
                        </>
                      )}
                      {canRegister && !isPending && approval !== "Rejected" && (d.registration_status !== "Registered" || !d.distributor_code) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mr-1"
                          onClick={() => openRegister(d)}
                          data-testid={`dm-register-distributor-${d.id}`}
                        >
                          <FileUp className="w-3.5 h-3.5 mr-1" /> Register
                        </Button>
                      )}
                      {canEdit && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(d)} data-testid={`dm-edit-distributor-${d.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Distributor" : "New Distributor"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Distributor Name *</Label><Input value={form.distributor_name} onChange={(e) => setForm({ ...form, distributor_name: e.target.value })} data-testid="dm-dist-name" /></div>
            <div className="space-y-1"><Label>GST Number</Label><Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} data-testid="dm-dist-gst" /></div>
            <div className="space-y-1"><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div className="space-y-1"><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="dm-dist-mobile" /></div>
            <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1 md:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="md:col-span-2">
              <StateDistrictSelect
                state={form.state}
                district={form.district}
                onStateChange={(v) => setForm({ ...form, state: v })}
                onDistrictChange={(v) => setForm({ ...form, district: v })}
                testidPrefix="dm-dist"
                required
              />
            </div>
            <div className="space-y-1"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="space-y-1">
              <Label>Active</Label>
              <Select value={form.active_status} onValueChange={(v) => setForm({ ...form, active_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Margin %</Label><Input type="number" value={form.distributor_margin} onChange={(e) => setForm({ ...form, distributor_margin: Number(e.target.value) })} /></div>
            <div className="space-y-1"><Label>Additional Margin %</Label><Input type="number" value={form.additional_margin} onChange={(e) => setForm({ ...form, additional_margin: Number(e.target.value) })} /></div>
            <div className="space-y-1"><Label>Credit Days</Label><Input type="number" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: Number(e.target.value) })} /></div>
            <div className="space-y-1"><Label>Security Deposit</Label><Input type="number" value={form.security_deposit} onChange={(e) => setForm({ ...form, security_deposit: Number(e.target.value) })} /></div>
            <div className="space-y-1 md:col-span-2"><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></div>
            <div className="space-y-1 md:col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={!!form.exclusive} onChange={(e) => setForm({ ...form, exclusive: e.target.checked })} id="dm-dist-exclusive" />
              <Label htmlFor="dm-dist-exclusive">Exclusive Distributor</Label>
            </div>
            {!editing && !isIBO && (
              <div className="md:col-span-2 mt-2 p-3 rounded-md border border-dashed border-primary/40 bg-primary/5 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <KeyRound className="w-4 h-4 text-primary" />
                  Login Credentials <span className="text-xs text-muted-foreground font-normal">(optional — creates a login for this distributor)</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Login Email</Label>
                    <Input
                      type="email"
                      value={form.login_email}
                      onChange={(e) => setForm({ ...form, login_email: e.target.value })}
                      placeholder="distributor@example.com"
                      data-testid="dm-dist-login-email"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Login Password</Label>
                    <Input
                      type="password"
                      value={form.login_password}
                      onChange={(e) => setForm({ ...form, login_password: e.target.value })}
                      placeholder="min 6 chars"
                      data-testid="dm-dist-login-password"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  When both fields are filled, the distributor can log in with these credentials and see ONLY their own city, parties, and dashboard.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="dm-save-distributor">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accountant / Admin — Register Distributor via Invoice */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Register Distributor</DialogTitle>
          </DialogHeader>
          {regTarget && (
            <div className="text-sm text-muted-foreground -mt-2">
              <span className="font-medium text-foreground">{regTarget.distributor_name}</span>
              {regTarget.city && ` · ${regTarget.city}${regTarget.state ? ", " + regTarget.state : ""}`}
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Invoice Type</Label>
              <Select value={regForm.invoice_type} onValueChange={(v) => setRegForm({ ...regForm, invoice_type: v })}>
                <SelectTrigger data-testid="dm-reg-dist-inv-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Proforma">Proforma Invoice</SelectItem>
                  <SelectItem value="Tax">Tax Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Invoice Number <span className="text-red-500">*</span></Label>
              <Input
                value={regForm.invoice_number}
                onChange={(e) => setRegForm({ ...regForm, invoice_number: e.target.value })}
                data-testid="dm-reg-dist-inv-number"
              />
            </div>
            <div className="space-y-1">
              <Label>Invoice Amount</Label>
              <Input
                type="number"
                value={regForm.invoice_amount}
                onChange={(e) => setRegForm({ ...regForm, invoice_amount: Number(e.target.value) })}
                data-testid="dm-reg-dist-inv-amount"
              />
            </div>
            <div className="space-y-1">
              <Label>Invoice File <span className="text-xs text-muted-foreground">(PDF/image)</span></Label>
              <Input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setRegForm({ ...regForm, file: e.target.files?.[0] || null })}
                data-testid="dm-reg-dist-file"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Registering assigns a <span className="font-mono">DIST-code</span> (if not already set) and marks status as Registered.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)}>Cancel</Button>
            <Button onClick={submitRegister} data-testid="dm-submit-register-dist">Register</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
